/**
 * PortfolioView.jsx - Main roster building view
 * Phase 3B Round 2 - Named export only
 * Beta v2 - Lock button removed (admin controls lock)
 * 
 * Extracted from GMRosterBuilderV2.jsx lines 320-371
 * FIXED: bottom-16 to sit above BottomNav
 */

import React from 'react';
import { ChevronLeft, Eye, RefreshCw } from 'lucide-react';
import { UnitWheel } from '../../../components/viz';
import RosterSlot from '../../../components/builder/RosterSlot';
import { WeekLockBanner } from '../../../components/common/WeekLockBanner';

export const PortfolioView = ({
  roster,
  poolUnits,
  availableBalance = 1000,
  usedUnits,
  filledSlots,
  totalSlots,
  isReady,
  isLocked,
  saving,
  onEditSlot,
  onReview,
  onBack,
  toRosterSlotSelection,
  // Phase 1A: Live outcomes
  liveScore,
  resolvedCount,
  lastUpdated,
  onRefreshOutcomes,
  isFullyResolved,
}) => {
  return (
    <div className="min-h-screen bg-slate-100 pb-36">
      <header className="bg-white shadow-sm px-4 py-2.5 flex items-center justify-between">
        <button 
          onClick={onBack} 
          className="p-1.5 text-slate-600 active:scale-90 min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <ChevronLeft size={22} />
        </button>
        <h1 className="text-base font-bold text-slate-900">Build Roster</h1>
        <div className="w-[50px]" /> {/* Spacer for header balance */}
      </header>
      
      {/* Beta v2: Admin-controlled lock banner */}
      <WeekLockBanner isLocked={isLocked} />
      
      <main className="p-3 space-y-3">
        <div className="bg-white rounded-xl shadow-sm p-3">
          <UnitWheel roster={roster} totalCapacity={availableBalance} />
        </div>
        
        {/* Phase 1A: Live outcomes status bar when locked */}
{isLocked && (
  <div className="bg-white rounded-xl shadow-sm p-3">
    <div className="flex items-center justify-between mb-2">
      <div>
        <p className="text-sm font-semibold text-slate-900">
          {liveScore != null ? `Score: $${liveScore}` : 'Awaiting Results'}
        </p>
        <p className="text-xs text-slate-500">
          {resolvedCount}/{roster.length} props resolved
          {lastUpdated && ` • Updated ${new Date(lastUpdated).toLocaleTimeString()}`}
        </p>
      </div>
      <button
        onClick={onRefreshOutcomes}
        className="p-2 text-slate-500 hover:text-indigo-600 active:scale-90"
        title="Refresh outcomes"
      >
        <RefreshCw size={18} />
      </button>
    </div>
    {/* P0.2: Visual progress bar */}
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div 
        className={`h-2 rounded-full transition-all duration-300 ${
          isFullyResolved ? 'bg-emerald-500' : 'bg-indigo-600'
        }`}
        style={{ width: `${roster.length > 0 ? (resolvedCount / roster.length) * 100 : 0}%` }}
      />
    </div>
  </div>
)}

        <div className="grid gap-2">
          {roster.map(s => {
            const selection = toRosterSlotSelection(s);
            return (
              <RosterSlot 
                key={s.slot} 
                slotId={s.slot}
                label={s.label}
                selection={selection}
                resolution={selection?.resolution || s.resolution || null}
                isLocked={isLocked}
                onClick={() => onEditSlot(s)} 
              />
            );
          })}
        </div>
      </main>
      
      {/* FIXED: bottom-16 to sit above BottomNav, z-40 to be above BottomNav's z-30 */}
      {/* Beta v2: Lock button removed - only Review button remains */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 p-3 z-40">
        <button 
          onClick={onReview} 
          disabled={filledSlots === 0}
          className="w-full bg-slate-100 text-slate-700 font-bold py-3 min-h-[48px] rounded-xl disabled:text-slate-300 flex items-center justify-center gap-2"
        >
          <Eye size={18} /> Review
        </button>
      </div>
    </div>
  );
};
